// A observação diária: como ela está, medida do mesmo jeito todo dia.
//
// Nasceu em 20/09, quando a família descreveu o que via depois da parada de
// 27/08 — "não lembra da gente", "dorme muito de dia", "aperta a mão, olha e
// desvia". Nenhuma dessas frases, sozinha, diz se está melhorando. Duas
// semanas delas, na mesma hora e com as mesmas três perguntas, dizem.
//
// As perguntas são o que dá para observar em quem fala pouco: obedecer,
// olhar, tentar se comunicar. Não medem memória — memória não se avalia em
// quem está dormindo, e foi por isso que a pergunta "ela lembra de mim?"
// virou estas três. O sono entra porque o ciclo invertido é o próprio achado:
// dormir de dia e ficar acordada à noite é a marca do delirium hipoativo.
//
// Isto é observação da família, não escala clínica. Não pontua, não conclui,
// não vira diagnóstico: mostra a linha, e quem lê a linha é a equipe.

export type RespostaDoDia = 'sim' | 'parcial' | 'nao';

export const RESPOSTAS: readonly RespostaDoDia[] = ['sim', 'parcial', 'nao'] as const;

/** Como cada resposta se chama no botão e no documento. */
export const ROTULO_RESPOSTA: Record<RespostaDoDia, string> = {
  sim: 'Sim',
  parcial: 'Mais ou menos',
  nao: 'Não',
};

/** Uma letra para a tabela do relatório, onde não cabe a palavra inteira. */
export const MARCA_RESPOSTA: Record<RespostaDoDia, string> = {
  sim: 'S', parcial: '±', nao: 'N',
};

export type ChaveDaPergunta = 'obedeceu' | 'olhou' | 'comunicou';

export interface PerguntaDoDia {
  chave: ChaveDaPergunta;
  /** A pergunta como ela aparece na tela, inteira. */
  pergunta: string;
  /** Curto, para o cabeçalho da tabela do relatório. */
  curto: string;
  /** O que fazer para descobrir — sem isso, cada pessoa responde outra coisa. */
  comoTestar: string;
  /** O que cada resposta quer dizer, para duas pessoas marcarem igual. */
  criterio: Record<RespostaDoDia, string>;
}

export const PERGUNTAS_DO_DIA: readonly PerguntaDoDia[] = [
  {
    chave: 'obedeceu',
    pergunta: 'Obedeceu a um comando simples?',
    curto: 'Obedeceu',
    comoTestar: 'Peça: “aperta minha mão”, “fecha os olhos”, “mostra a língua”.',
    criterio: {
      sim: 'fez o que foi pedido, sem precisar repetir muito',
      parcial: 'fez depois de insistir, ou fez um e não os outros',
      nao: 'não fez nenhum',
    },
  },
  {
    chave: 'olhou',
    pergunta: 'Acompanhou com os olhos e reagiu ao nome?',
    curto: 'Olhou',
    comoTestar: 'Chame pelo nome e passe a mão devagar na frente dos olhos dela.',
    criterio: {
      sim: 'virou, olhou e sustentou o olhar',
      parcial: 'olhou e desviou logo, ou só às vezes',
      nao: 'não reagiu',
    },
  },
  {
    chave: 'comunicou',
    pergunta: 'Tentou se comunicar?',
    curto: 'Comunicou',
    comoTestar: 'Vale falar, sussurrar, mexer a boca, fazer que sim ou não, apontar.',
    criterio: {
      sim: 'falou ou se fez entender',
      parcial: 'tentou, mas não deu para entender',
      nao: 'não tentou',
    },
  },
];

export interface ObservacaoDoDia {
  dia: string;
  obedeceu: RespostaDoDia;
  olhou: RespostaDoDia;
  comunicou: RespostaDoDia;
  sonoDiaHoras: number | null;
  sonoNoiteHoras: number | null;
  observacao: string | null;
}

/**
 * Quantos "sim" o dia teve — 0 a 3.
 *
 * NÃO é pontuação clínica e não vira escala: é o número que permite dizer
 * "três dias seguidos com 3 de 3" numa frase curta. Quem decide o que isso
 * significa é a equipe, com o exame na frente.
 */
export const quantosSim = (o: ObservacaoDoDia): number =>
  PERGUNTAS_DO_DIA.filter((p) => o[p.chave] === 'sim').length;

/** As horas de sono como "6 h de dia · 2 h à noite", omitindo o que falta. */
export function sonoEmTexto(o: ObservacaoDoDia): string | null {
  const partes: string[] = [];
  if (o.sonoDiaHoras !== null) partes.push(`${formatarHoras(o.sonoDiaHoras)} de dia`);
  if (o.sonoNoiteHoras !== null) partes.push(`${formatarHoras(o.sonoNoiteHoras)} à noite`);
  return partes.length > 0 ? partes.join(' · ') : null;
}

const formatarHoras = (h: number): string =>
  `${h.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} h`;

/**
 * O que a sequência mostra, em uma frase — ou null quando ainda não mostra nada.
 *
 * Compara os três dias mais recentes com os três anteriores. Menos de quatro
 * dias não dá para comparar, e inventar tendência com dois pontos é pior do
 * que não dizer nada: a família leria "melhorou" num dia que foi só melhor
 * que o anterior.
 */
export function tendenciaDaEvolucao(observacoes: ObservacaoDoDia[]): string | null {
  if (observacoes.length < 4) return null;
  // Ordena do mais recente para o mais antigo, sem mexer na lista de quem chamou.
  const ordenadas = [...observacoes].sort((a, b) => (a.dia < b.dia ? 1 : -1));
  const media = (lista: ObservacaoDoDia[]) =>
    lista.reduce((soma, o) => soma + quantosSim(o), 0) / lista.length;

  const recentes = ordenadas.slice(0, 3);
  const antes = ordenadas.slice(3, 6);
  const diferenca = media(recentes) - media(antes);

  const de = `${formatarMedia(media(antes))} para ${formatarMedia(media(recentes))} de 3`;
  if (diferenca >= 0.67) return `Os últimos três dias têm mais respostas que os anteriores — ${de}.`;
  if (diferenca <= -0.67) return `Os últimos três dias têm menos respostas que os anteriores — ${de}. Vale contar isso à equipe.`;
  return `Sem mudança clara nos últimos dias — ${de}.`;
}

const formatarMedia = (n: number): string => n.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
